import { Duration } from "./duration";

/**
 * A utility class to represent a date range.
 * A range is made of a "from" date and a "to" date.
 * For consistency and ease of use, object made out of this class are immutable.
 */
export class DatesRange
{
    protected fromDate: Date;
    protected toDate: Date;

    public get from(): Date
    {
        return new Date(this.fromDate.getTime());
    }

    public get to(): Date
    {
        return new Date(this.toDate.getTime());
    }

    public get timeInside(): number
    {
        return this.toDate.getTime() - this.fromDate.getTime();
    }

    public constructor(from: Date, to: Date)
    {
        // From MUST NOT be after to, swap them if it is not the case
        if (to < from) {
            const tmp = from;
            from = to;
            to = tmp;
        }

        this.fromDate = from;
        this.toDate = to;
    }

    /**
     * Factory method to make a range from strings. Strings must be parseable
     * by the constructor of the native JS Date object.
     * 
     * @param from          One of the end of the range
     * @param to            The other end of the range
     */
    public static fromStrings(from: string, to: string): DatesRange
    {
        return new this(new Date(from), new Date(to));
    }

    /**
     * Factory method to make a range starting at a given date and having its other
     * bound calculated from a duration.
     * 
     * @param from          The start date of the range
     * @param duration      The duration of the range, may be negative
     */
    public static fromDuration(from: Date, duration: Duration): DatesRange
    {
        return new this(from, duration.addTo(from));
    }

    /**
     * Check whether a date is inside this range.
     * 
     * @param date          The date to check
     */
    public containsDate(date: Date): boolean
    {
        return this.fromDate <= date && this.toDate >= date;
    }

    /**
     * Check whether another range is entirely contained inside this range.
     * 
     * @param other         The other range to check
     */
    public containsRange(other: DatesRange): boolean
    {
        return other.fromDate >= this.fromDate && other.toDate <= this.toDate;
    }

    /**
     * Check whether "this" range is entirely before another one.
     * If the range touches, this function will return false.
     * 
     * @param other         The other range to compare with
     */
    public isBeforeRange(other: DatesRange): boolean
    {
        return this.toDate < other.fromDate;
    }

    /**
     * Check whether "this" range is directly before another one (their ends are same).
     * 
     * @param other         The other range to compare with
     */
    public isRightBeforeRange(other: DatesRange): boolean
    {
        // /!\ In JS, == or === cannot be used to compare date objects
        return this.toDate.getTime() === other.fromDate.getTime();
    }

    /**
     * Check whether "this" range is entirely after another one.
     * 
     * @param other         The other range to compare with
     */
    public isAfterRange(other: DatesRange): boolean
    {
        return this.fromDate > other.toDate;
    }

    /**
     * Check whether "this" range is directly after another one (their ends are same).
     * 
     * @param other         The other range to compare with
     */
     public isRightAfterRange(other: DatesRange): boolean
     {
         // /!\ In JS, == or === cannot be used to compare date objects
         return this.fromDate.getTime() === other.toDate.getTime();
     }

     /**
     * Check whether the current range is overlapping another one
     * 
     * @param other         The other range to check overlap with
     */
    public isOverlapping(other: DatesRange): boolean
    {
        return this.getOverlappingRange(other) !== null;
    }

    /**
     * Get a new range corresponding to the overlapping period of this range
     * and another one. Returns null if there is no overlap between the ranges.
     * 
     * @param other         The other range to check overlap with
     * @returns             The overlapping of both ranges or null
     */
    public getOverlappingRange(other: DatesRange): DatesRange|null
    {
        if (this.fromDate <= other.fromDate && this.toDate >= other.toDate) {
            return other;
        }

        if (this.fromDate < other.fromDate) {
            if (this.toDate < other.fromDate) {
                return null;
            }

            return new DatesRange(other.from, this.toDate);
        }

        if (this.toDate > other.toDate) {
            if (this.fromDate > other.toDate) {
                return null;
            }

            return new DatesRange(this.from, other.toDate);
        }

        return this;
    }

    /**
     * Given another range, returns the current range with any overlap with the other
     * one removed. The returned value is an array of ranges. The amount of ranges in the array
     * depends on the following conditions:
     * - 0: the "other" range completely overlaps "this" range
     * - 1: both ranges have no overlap ("this" is returned) or "other" overlaps either the beginning or the end of "this"
     * - 2: "other" is included inside "this", thus the parts of "this" before and after it are returned.
     * 
     * @param other         The range to subtract
     * @returns             An array of range resulting of the subtraction
     */
    public subtract(other: DatesRange): DatesRange[]
    {
        const overlap = this.getOverlappingRange(other);

        // Check if "other" is completely outsite of "this" range
        if (overlap === null) {
            return [ this ];
        }

        const diff = [];

        // Check if "this" range overflows "other" in the past
        if (this.fromDate < overlap.fromDate) {
            diff.push(new DatesRange(this.from, overlap.from));
        }

        // Check if "this" range overflows "other" in the future
        if (this.toDate > overlap.toDate) {
            diff.push(new DatesRange(overlap.to, this.to));
        }

        return diff;
    }

    /**
     * Given this range and another one, returns the smallest single range containing both of them.
     * 
     * @param other         The range to add
     * @returns             The encompassing range
     */
    public mergeAndFill(other: DatesRange): DatesRange
    {
        const minFrom = (this.fromDate < other.fromDate) ? this.from : other.from;
        const maxTo = (this.toDate > other.toDate) ? this.to : other.to;

        return new DatesRange(minFrom, maxTo);
    }

    /**
     * Returns the the resulting range of the current range with some time added before it.
     * 
     * @param time          The time to add before the range
     * @returns             The new range with time added before
     */
    public addTimeBefore(time: Duration): DatesRange
    {
        return new DatesRange(time.subFrom(this.from), this.toDate);
    }

    /**
     * Returns the the resulting range of the current range with some time added after it.
     * 
     * @param time          The time to add after the range
     * @returns             The new range with time added after
     */
    public addTimeAfter(time: Duration): DatesRange
    {
        return new DatesRange(this.fromDate, time.addTo(this.to));
    }

    /**
     * Get a new range of a given duration right before the current range.
     * 
     * @param time          The length of the new range
     * @returns             The new range ending before this one
     */
    public getRangeBefore(time: Duration): DatesRange
    {
        return new DatesRange(time.subFrom(this.from), this.fromDate);
    }

    /**
     * Get a new range of a given duration right after the current range.
     * 
     * @param time          The time in milliseconds of the new range
     * @returns             The new range starting after this one
     */
    public getRangeAfter(time: Duration): DatesRange
    {
        return new DatesRange(this.toDate, time.addTo(this.to));
    }

    /**
     * Get a new range that is offset of the current one by a given time.
     * 
     * @param duration      The offset to apply to the new range (can be negative)
     */
    public getOffsetRange(duration: Duration): DatesRange
    {
        return new DatesRange(
            new Date(this.fromDate.getTime() + duration.milliseconds),
            new Date(this.toDate.getTime() + duration.milliseconds)
        );
    }
}
